import React, { Suspense, useEffect } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { useLoggedInUser } from '@tcc/shared-data-lib';
import { FieldGuideUi } from '@tcc/field-guide';
import { getURLFromHash } from '../../utils/env';
import { ROUTE } from '../../constants/routes';
import { AuthPluginContainer } from '../../containers/AuthPluginContainer';
import { ApplicationView } from '../ApplicationView';
import { AuthorizedRoute } from '../AuthorizedRoute';
import { useUserLoggedInConfigs } from '../../utils/sharedData';
import { SpinnerLoading } from '../../components/SpinnerLoading/SpinnerLoading';
import { getUserData, logout } from '../../utils/wam';
import { injectCookieConsentBanner } from '../../utils/injectCookieConsentBanner';
import { useBoot } from '../../boot';
import { bootSteps } from '../../boot/bootSteps';

/*
 During react-router's initialization, it calls window.location.replace('#/')
 if we don't have an initial hash.
 During release, we add a <base> tag to the head of the index.html file that specifies
 the commit hash that is to be released. Using replace() with a relative path
 also adds the base path to the end of the url, which we do not want. So, we set the
 hash path manually so react-router does not unintentionally redirect us to the wrong path.
*/
if (window.location.hash === '') {
  window.location.hash = '/';
}

export const AppRouter = () => {
  const location = useLocation();
  const { pathname } = location;
  const isOnAuth = pathname.includes(ROUTE.Auth);
  const hasPreso = getUserData();

  const bootState = useBoot(bootSteps);

  useEffect(() => {
    injectCookieConsentBanner();
  }, []);

  const { user, error: userError, isLoading } = useLoggedInUser({
    ...useUserLoggedInConfigs,
    config: {
      ...useUserLoggedInConfigs.config,
      skip: isOnAuth || !hasPreso
    }
  });

  const history = useHistory();

  // Build auth redirect — always preserves the current URL so tcc-auth-plugin
  // can document.location.assign() back after login.
  const authRedirect = `${ROUTE.Auth}/login?to=${encodeURIComponent(window.location.href)}`;

  // Scope html tag with app name as class to avoid css leaks between plugins.
  useEffect(() => {
    const currentMainApp = Object.values(ROUTE).find(appPath => pathname.includes(appPath));

    if (currentMainApp) {
      const htmlNewClassName = currentMainApp.replace('/', '');

      const htmlTag = document.getElementsByTagName('html')[0];

      // Clear previous classes
      htmlTag.classList.remove(...htmlTag.classList);

      htmlTag.classList.add(htmlNewClassName);
    }
  }, [pathname]);

  useEffect(() => {
    if (userError && !isOnAuth) {
      logout();
      history.push(authRedirect);
    }
  }, [userError, isOnAuth, history, authRedirect]);

  if (isLoading || bootState.isBooting) {
    return <SpinnerLoading />;
  }

  return (
    <Suspense fallback={<SpinnerLoading />}>
      <FieldGuideUi userUUID={user?.details.uuid || ''} />
      <Switch>
        <Route path={ROUTE.Auth} component={AuthPluginContainer} />
        {!isOnAuth && !!hasPreso && (
          <AuthorizedRoute component={ApplicationView} redirect={authRedirect} isEnabled={!!user} />
        )}
        <Route path='*'>
          {() => {
            window.location.href = getURLFromHash(authRedirect);
            return null;
          }}
        </Route>
      </Switch>
    </Suspense>
  );
};
